import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { formatGrams, formatSignedWeightValue } from '@/lib/format/format-weight';
import { feltBalanceWeight } from '@/lib/piano/touch-design/felt-balance-weight';
import { computeKeyboardDrawing, WHITE_KEY_LENGTH_RATIO } from '@/components/piano/keyboard-drawing';
import { KeyboardPlate, keybedHeight } from '@/components/piano/KeyboardPlate';
import { useTextScale } from '@/hooks/use-text-scale';
import { useTranslation } from '@/hooks/use-translation';
import { diamondsPath, linePath, plotX, plotY, type PlotBox } from './figures/plot';
import type { HomeState } from './hooks/use-home-state';

/** The drawing is laid out in white-key widths of 20, as the charts are. */
const WHITE_KEY_WIDTH = 20;
/** Keys of the instrument's proportions, as under every chart. */
const KEY_HEIGHT = WHITE_KEY_WIDTH * WHITE_KEY_LENGTH_RATIO;
const PLOT_TOP = 16;
const PLOT_BOTTOM = 141;
const KEYBOARD_TOP = 182;

/** The scale the figure opens on; a heavier piano widens it. */
const WEIGHT_FLOOR = 40;
const WEIGHT_CEILING = 80;
const WEIGHT_STEP = 10;

/** Below this spread the design lands on one weight, and can be named. */
const FLAT_TARGET_TOLERANCE = 0.5;
const DOT_RADIUS = 3.4;
interface Point {
  x: number;
  y: number;
}

/**
 * The balance weight of the piano on screen, key by key, over its own
 * keyboard.
 *
 * The home page is the one place that shows a whole instrument at a glance,
 * so the figure follows the reader: their measurements as they take them,
 * then the touch their design is heading for. It is ruled by the balance
 * rail like every other figure of the application.
 */
export const BalanceWeightFigure = t0 => {
  const $ = _c(90);
  const {
    description,
    home
  } = t0;
  const {
    currentLanguage,
    t
  } = useTranslation();
  let t1;
  if ($[0] !== home.analyzedKeyboard || $[1] !== home.designedKeyboard || $[2] !== home.nextKeyToMeasure || $[3] !== home.stage) {
    bb0: {
      const keys = home.analyzedKeyboard.getKeys();
      const geometry = computeKeyboardDrawing(keys, {
        keyHeight: KEY_HEIGHT,
        whiteKeyWidth: WHITE_KEY_WIDTH
      });
      const measuredWeights = keys.map(_temp).filter(_temp2);
      const targetByNumber = new Map();
      if (home.stage === "demo" || home.stage === "designed") {
        for (const key_0 of home.designedKeyboard.getKeys()) {
          const aim = feltBalanceWeight(key_0.payload);
          if (aim !== null) {
            targetByNumber.set(key_0.number, aim);
          }
        }
      }
      const targetWeights = [...targetByNumber.values()];
      if (measuredWeights.length < 2) {
        t1 = null;
        break bb0;
      }
      const min = Math.floor(Math.min(WEIGHT_FLOOR, ...measuredWeights, ...targetWeights) / WEIGHT_STEP) * WEIGHT_STEP;
      const max = Math.ceil(Math.max(WEIGHT_CEILING, ...measuredWeights, ...targetWeights) / WEIGHT_STEP) * WEIGHT_STEP;
      const box = {
        from: 0,
        height: PLOT_BOTTOM - PLOT_TOP,
        max,
        min,
        to: keys.length - 1,
        width: (keys.length - 1) * geometry.slotWidth,
        x: geometry.slotWidth / 2,
        y: PLOT_TOP
      };
      const measuredValues = keys.map(_temp3);
      const targetValues = keys.map(key_2 => targetByNumber.get(key_2.number) ?? null);
      const measured = [];
      const target = [];
      const gapColumns = [];
      keys.forEach((_, index) => {
        const x = plotX(box, index);
        const weight_0 = measuredValues[index] ?? null;
        const aim_0 = targetValues[index] ?? null;
        if (weight_0 !== null) {
          measured.push({
            x,
            y: plotY(box, weight_0)
          });
        }
        if (aim_0 !== null) {
          target.push({
            x,
            y: plotY(box, aim_0)
          });
        }
        if (weight_0 !== null && aim_0 !== null) {
          gapColumns.push(`M${x - geometry.slotWidth / 2} ${plotY(box, weight_0)}` + `h${geometry.slotWidth}V${plotY(box, aim_0)}h${-geometry.slotWidth}z`);
        }
      });
      const differences = keys.map(key_3 => {
        const weight_1 = feltBalanceWeight(key_3.payload);
        const aim_1 = targetByNumber.get(key_3.number);
        return weight_1 === null || aim_1 === undefined ? null : aim_1 - weight_1;
      }).filter(_temp4);
      const targetSpread = targetWeights.length === 0 ? 0 : Math.max(...targetWeights) - Math.min(...targetWeights);
      const t2 = gapColumns.join("");
      const t3 = (() => {
        const anchor = measured[Math.floor(measured.length / 4)];
        const aim_2 = target[Math.floor(target.length / 4)];
        if (!anchor || !aim_2 || Math.abs(aim_2.y - anchor.y) < 18) {
          return null;
        }
        const head = aim_2.y > anchor.y ? -5 : 5;
        const tip = aim_2.y - Math.sign(aim_2.y - anchor.y) * 4;
        return {
          path: `M${anchor.x} ${anchor.y + Math.sign(aim_2.y - anchor.y) * 6}V${tip}` + `m${-4} ${head}l4 ${-head}l4 ${head}`,
          x: anchor.x + 8,
          y: (anchor.y + aim_2.y) / 2
        };
      })();
      let t4;
      if ($[5] !== max || $[6] !== min) {
        let t5;
        if ($[8] !== min) {
          t5 = (__0, index_0) => min + index_0 * WEIGHT_STEP;
          $[8] = min;
          $[9] = t5;
        } else {
          t5 = $[9];
        }
        t4 = Array.from({
          length: (max - min) / WEIGHT_STEP + 1
        }, t5);
        $[5] = max;
        $[6] = min;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      const t5 = diamondsPath(measuredValues, box, DOT_RADIUS);
      const t6 = linePath(measuredValues, box);
      const t7 = Math.max(...measuredWeights);
      const t8 = Math.min(...measuredWeights);
      const t9 = measured.map(_temp6).join("");
      const t10 = linePath(targetValues, box);
      let t11;
      if ($[10] !== target) {
        t11 = target.map(_temp7);
        $[10] = target;
        $[11] = t11;
      } else {
        t11 = $[11];
      }
      t1 = {
        gap: t2,
        gapArrow: t3,
        geometry,
        gridLines: t4,
        height: KEYBOARD_TOP + keybedHeight(geometry),
        isTargetFlat: targetSpread <= FLAT_TARGET_TOLERANCE,
        meanDifference: differences.length === 0 ? null : differences.reduce(_temp5, 0) / differences.length,
        measuredDots: t5,
        measuredLine: t6,
        measuredMax: t7,
        measuredMin: t8,
        nextKey: home.nextKeyToMeasure === null ? null : (() => {
          const placed = geometry.keysByNumber.get(home.nextKeyToMeasure.number);
          return placed?.path ?? null;
        })(),
        stems: t9,
        targetLine: t10,
        targetWeight: targetWeights.length === 0 ? null : targetWeights[0],
        ties: t11.join(""),
        width: geometry.width
      };
    }
    $[0] = home.analyzedKeyboard;
    $[1] = home.designedKeyboard;
    $[2] = home.nextKeyToMeasure;
    $[3] = home.stage;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const figure = t1;
  const textScale = useTextScale(figure?.width ?? 0);
  if (!figure) {
    return null;
  }
  const hasTarget = figure.targetLine !== "";
  const remaining = home.total - home.measured;
  let t2;
  if ($[12] !== description) {
    t2 = <span className="ptl-label">{description}</span>;
    $[12] = description;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== currentLanguage || $[15] !== figure.isTargetFlat || $[16] !== figure.measuredMax || $[17] !== figure.measuredMin || $[18] !== figure.targetWeight || $[19] !== hasTarget || $[20] !== home.measured || $[21] !== home.total || $[22] !== t) {
    t3 = hasTarget ? <span className="flex flex-wrap items-center gap-x-5 gap-y-1.5 font-mono text-[11.5px] text-muted-foreground"><span className="inline-flex items-center gap-1.5"><svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true"><path d="M5 0l5 5-5 5-5-5z" className="fill-ptl-ink-3" /></svg>{t("measured \xB7 from {{min}} to {{max}}", {
          max: formatGrams(figure.measuredMax, currentLanguage),
          min: formatGrams(figure.measuredMin, currentLanguage)
        })}</span><span className="inline-flex items-center gap-1.5"><span className="h-[2.4px] w-[18px] bg-ptl-brass" />{figure.isTargetFlat && figure.targetWeight !== null ? t("after adjustment \xB7 {{weight}}", {
          weight: formatGrams(figure.targetWeight, currentLanguage)
        }) : t("after adjustment (expected)")}</span></span> : <span className="font-mono text-[11.5px] text-muted-foreground">{t("{{measured}} keys measured out of {{total}}", {
        measured: home.measured,
        total: home.total
      })}</span>;
    $[14] = currentLanguage;
    $[15] = figure.isTargetFlat;
    $[16] = figure.measuredMax;
    $[17] = figure.measuredMin;
    $[18] = figure.targetWeight;
    $[19] = hasTarget;
    $[20] = home.measured;
    $[21] = home.total;
    $[22] = t;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== t2 || $[25] !== t3) {
    t4 = <figcaption className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1.5 pb-1.5 text-left">{t2}{t3}</figcaption>;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  const t5 = `0 0 ${figure.width} ${figure.height}`;
  let t6;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <linearGradient id="ptl-home-stem" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="var(--ptl-ink-3)" stopOpacity="0.3" /><stop offset="1" stopColor="var(--ptl-ink-3)" stopOpacity="0.03" /></linearGradient>;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <linearGradient id="ptl-home-tie" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="var(--ptl-brass)" stopOpacity="0.4" /><stop offset="1" stopColor="var(--ptl-brass)" stopOpacity="0.04" /></linearGradient>;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <defs>{t6}{t7}<pattern id="ptl-home-hatch" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45)"><path d="M0 0V7" strokeWidth="1.3" className="stroke-ptl-brass" strokeOpacity="0.4" /></pattern></defs>;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== figure.gridLines || $[31] !== figure.width || $[32] !== t) {
    let t10;
    if ($[34] !== figure.gridLines.length || $[35] !== figure.width || $[36] !== t) {
      t10 = (weight_2, index_1) => {
        const row = PLOT_BOTTOM - index_1 * (PLOT_BOTTOM - PLOT_TOP) / (figure.gridLines.length - 1);
        const isNamed = (figure.gridLines.length - 1 - index_1) % 2 === 0;
        return <g key={weight_2}><path d={`M34 ${row}H${figure.width}`} strokeWidth="1" strokeDasharray="2 4" fill="none" className="stroke-border" />{isNamed ? <text x="0" y={row + 3.5} style={{
            fontSize: "calc(10px * var(--ptl-text-scale, 1))"
          }} className="fill-ptl-ink-3 font-mono max-md:hidden">{t("{{weight}} g", {
              weight: weight_2
            })}</text> : null}</g>;
      };
      $[34] = figure.gridLines.length;
      $[35] = figure.width;
      $[36] = t;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    t9 = figure.gridLines.map(t10);
    $[30] = figure.gridLines;
    $[31] = figure.width;
    $[32] = t;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[38] !== figure.gap || $[39] !== hasTarget) {
    t10 = hasTarget ? <><path d={figure.gap} fill="url(#ptl-home-hatch)" /><path d={figure.gap} className="fill-ptl-brass" opacity="0.06" /></> : null;
    $[38] = figure.gap;
    $[39] = hasTarget;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  const t11 = hasTarget ? figure.ties : figure.stems;
  const t12 = `url(#${hasTarget ? "ptl-home-tie" : "ptl-home-stem"})`;
  let t13;
  if ($[41] !== t11 || $[42] !== t12) {
    t13 = <path d={t11} strokeWidth="1" fill="none" stroke={t12} />;
    $[41] = t11;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== figure.measuredLine) {
    t14 = <path d={figure.measuredLine} strokeWidth="1.2" fill="none" opacity="0.6" className="stroke-ptl-ink-3" />;
    $[44] = figure.measuredLine;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== figure.measuredDots) {
    t15 = <path d={figure.measuredDots} className="fill-muted-foreground" />;
    $[46] = figure.measuredDots;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== figure.targetLine || $[49] !== hasTarget) {
    t16 = hasTarget ? <path d={figure.targetLine} strokeWidth="2.6" fill="none" className="stroke-ptl-brass" /> : null;
    $[48] = figure.targetLine;
    $[49] = hasTarget;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== currentLanguage || $[52] !== figure.gapArrow || $[53] !== figure.meanDifference || $[54] !== hasTarget || $[55] !== t) {
    t17 = hasTarget && figure.gapArrow && figure.meanDifference !== null ? <g><path d={figure.gapArrow.path} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" className="stroke-ptl-brass" /><text x={figure.gapArrow.x} y={figure.gapArrow.y} style={{
        fontSize: "calc(11.5px * var(--ptl-text-scale, 1))",
        paintOrder: "stroke",
        stroke: "var(--background)",
        strokeWidth: "calc(3.5px * var(--ptl-text-scale, 1))"
      }} className="fill-ptl-brass font-mono font-semibold">{t("{{weight}} g on average", {
          weight: formatSignedWeightValue(figure.meanDifference, currentLanguage)
        })}</text></g> : null;
    $[51] = currentLanguage;
    $[52] = figure.gapArrow;
    $[53] = figure.meanDifference;
    $[54] = hasTarget;
    $[55] = t;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== figure.nextKey || $[58] !== home.stage) {
    t18 = home.stage === "measuring" && figure.nextKey ? <path d={figure.nextKey} className="fill-ptl-brass" /> : null;
    $[57] = figure.nextKey;
    $[58] = home.stage;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== figure.geometry || $[61] !== t18) {
    t19 = <g transform={`translate(0, ${KEYBOARD_TOP})`}><KeyboardPlate geometry={figure.geometry}>{t18}</KeyboardPlate></g>;
    $[60] = figure.geometry;
    $[61] = t18;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== figure.width || $[64] !== home.nextKeyToMeasure || $[65] !== home.stage || $[66] !== remaining || $[67] !== t) {
    t20 = home.stage === "measuring" ? <g><rect x={figure.width / 2 - 170} y={PLOT_BOTTOM - 74} width="340" height="58" rx="4" strokeWidth="1" strokeDasharray="4 4" className="fill-card stroke-input" fillOpacity="0.86" /><text x={figure.width / 2} y={PLOT_BOTTOM - 48} textAnchor="middle" className="fill-muted-foreground font-serif text-[19px] italic">{t("{{count}} keys left to measure", {
          count: remaining
        })}</text><text x={figure.width / 2} y={PLOT_BOTTOM - 28} textAnchor="middle" className="fill-ptl-ink-3 font-mono text-[10.5px]">{home.nextKeyToMeasure ? t("next: {{note}} \xB7 key {{number}}", {
          note: home.nextKeyToMeasure.name,
          number: home.nextKeyToMeasure.number
        }) : null}</text></g> : null;
    $[63] = figure.width;
    $[64] = home.nextKeyToMeasure;
    $[65] = home.stage;
    $[66] = remaining;
    $[67] = t;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  if ($[69] !== figure.width || $[70] !== home.stage || $[71] !== t) {
    t21 = home.stage === "measured" ? <g><rect x={figure.width / 2 - 125} y={PLOT_BOTTOM - 38} width="250" height="25" rx="12.5" strokeWidth="1.3" strokeDasharray="4 3" className="fill-card stroke-ptl-ink-3" /><text x={figure.width / 2} y={PLOT_BOTTOM - 21} textAnchor="middle" className="fill-muted-foreground font-mono text-[11.5px]">{t("Target: to be set in Design")}</text></g> : null;
    $[69] = figure.width;
    $[70] = home.stage;
    $[71] = t;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  let t22;
  if ($[73] !== description || $[74] !== t10 || $[75] !== t13 || $[76] !== t14 || $[77] !== t15 || $[78] !== t16 || $[79] !== t17 || $[80] !== t19 || $[81] !== t20 || $[82] !== t21 || $[83] !== t5 || $[84] !== t9 || $[85] !== textScale) {
    t22 = <svg ref={textScale} viewBox={t5} className="block h-auto w-full" role="img" aria-label={description}>{t8}{t9}{t10}{t13}{t14}{t15}{t16}{t17}{t19}{t20}{t21}</svg>;
    $[73] = description;
    $[74] = t10;
    $[75] = t13;
    $[76] = t14;
    $[77] = t15;
    $[78] = t16;
    $[79] = t17;
    $[80] = t19;
    $[81] = t20;
    $[82] = t21;
    $[83] = t5;
    $[84] = t9;
    $[85] = textScale;
    $[86] = t22;
  } else {
    t22 = $[86];
  }
  let t23;
  if ($[87] !== t22 || $[88] !== t4) {
    t23 = <figure className="m-0 mt-[72px] w-full max-w-[1080px]">{t4}{t22}</figure>;
    $[87] = t22;
    $[88] = t4;
    $[89] = t23;
  } else {
    t23 = $[89];
  }
  return t23;
};
function _temp(key) {
  return feltBalanceWeight(key.payload);
}
function _temp2(weight) {
  return weight !== null;
}
function _temp3(key_1) {
  return feltBalanceWeight(key_1.payload);
}
function _temp4(value) {
  return value !== null;
}
function _temp5(total, value_0) {
  return total + value_0;
}
function _temp6(point) {
  return `M${point.x} ${point.y + 4}V${KEYBOARD_TOP - 2}`;
}
function _temp7(point_0) {
  return `M${point_0.x} ${point_0.y + 2}V${KEYBOARD_TOP - 2}`;
}